import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Stack,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { panelNeutral, panelNeutralHover } from '../../../theme/gridSurfaces';
import { UnifiedNote } from './types';

interface NoteDetailModalProps {
  open: boolean;
  note: UnifiedNote | null;
  onClose: () => void;
  onViewSource?: (note: UnifiedNote) => void;
}

export const NoteDetailModal: React.FC<NoteDetailModalProps> = ({
  open,
  note,
  onClose,
  onViewSource,
}) => {
  if (!note) return null;

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'clinical':
        return '#4caf50';
      case 'billing':
        return '#2196f3';
      case 'delivery':
        return '#ff9800';
      case 'troubleshooting':
        return '#f44336';
      case 'subscription':
        return '#9c27b0';
      default:
        return '#757575';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" component="span">
              Note Details
            </Typography>
            <Chip
              label={note.category}
              size="small"
              sx={{
                bgcolor: getTypeColor(note.type),
                color: 'white',
                fontWeight: 600,
              }}
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Timestamp Information */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Timestamps
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip
                label={`Created: ${format(new Date(note.timestamp), 'MMM dd, yyyy HH:mm')}`}
                size="small"
                variant="outlined"
              />
              {note.metadata?.updatedAt && (
                <Chip
                  label={`Updated: ${format(new Date(note.metadata.updatedAt), 'MMM dd, yyyy HH:mm')}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Source Information */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Source Information
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={note.source} size="small" color="primary" variant="outlined" />
              {note.metadata?.orderNumber && (
                <Chip label={`Order #${note.metadata.orderNumber}`} size="small" variant="outlined" />
              )}
              {note.metadata?.rxNumber && (
                <Chip label={`Rx #${note.metadata.rxNumber}`} size="small" variant="outlined" />
              )}
              {note.metadata?.medicationName && (
                <Chip
                  label={note.metadata.medicationName}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
              {note.metadata?.workflowStage && (
                <Chip
                  label={note.metadata.workflowStage}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Author Information */}
          {note.author && (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Author
                </Typography>
                <Chip label={note.author} size="small" />
              </Box>
              <Divider />
            </>
          )}

          {/* Note Content */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Content
            </Typography>
            <Box
              sx={{
                p: 2,
                // GP-1232: `grey.50`/`grey.200` stay near-white in the dark
                // palette, so the note body rendered as a white slab under
                // light text. Mode-aware panel tokens instead.
                bgcolor: panelNeutral,
                borderRadius: 1,
                border: '1px solid',
                borderColor: panelNeutralHover,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'Roboto, sans-serif',
                }}
              >
                {note.content}
              </Typography>
            </Box>
          </Box>

          {/* Additional Metadata */}
          {note.metadata && Object.keys(note.metadata).length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Additional Information
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {note.metadata.clinicalVerifiedAt && (
                    <Chip
                      label={`Verified: ${format(new Date(note.metadata.clinicalVerifiedAt), 'MMM dd, yyyy')}`}
                      size="small"
                      color="success"
                    />
                  )}
                  {note.metadata.clinicalVerifiedBy && (
                    <Chip
                      label={`By: ${note.metadata.clinicalVerifiedBy}`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {note.metadata.issueType && (
                    <Chip label={`Issue: ${note.metadata.issueType}`} size="small" color="error" />
                  )}
                  {note.metadata.priority && (
                    <Chip label={`Priority: ${note.metadata.priority}`} size="small" color="warning" />
                  )}
                  {note.metadata.status && (
                    <Chip label={`Status: ${note.metadata.status}`} size="small" />
                  )}
                  {note.metadata.resolvedAt && (
                    <Chip
                      label={`Resolved: ${format(new Date(note.metadata.resolvedAt), 'MMM dd, yyyy')}`}
                      size="small"
                      color="success"
                    />
                  )}
                  {note.metadata.deliveryDate && (
                    <Chip
                      label={`Delivery: ${format(new Date(note.metadata.deliveryDate), 'MMM dd, yyyy')}`}
                      size="small"
                    />
                  )}
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        {onViewSource && (note.sourceType === 'order' || note.sourceType === 'billing_item') && (
          <Button
            startIcon={<OpenInNewIcon />}
            onClick={() => onViewSource(note)}
            variant="outlined"
            size="small"
          >
            View {note.sourceType === 'order' ? 'Order' : 'Billing Item'}
          </Button>
        )}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
